import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { LegalLinks } from "@/components/LegalLinks";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LANGUAGES } from "@/i18n/translations";
import { Globe, Ruler, SunMoon, Bell, UserRound, LogOut, Pencil, Users, ChevronRight, Moon, Lock, BellRing, ShieldCheck, MapPin, CreditCard, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { formatMoney } from "@/lib/billing";
import { BillingCountrySelect } from "@/components/BillingCountrySelect";
import { Input } from "@/components/ui/input";
import { isPaid } from "@/lib/planActions";
import { notifState } from "@/lib/reminders";
import { JournalReminderRow } from "@/components/journal/JournalReminderRow";
import { pushSupported, isIOS, isStandalone, detectTimezone, currentSubscription, subscribePush, unsubscribePush } from "@/lib/push";

const TIMEZONES = (() => { try { return Intl.supportedValuesOf("timeZone"); } catch { return ["Europe/Zurich", "Europe/Paris", "Europe/Berlin", "Europe/Rome", "Europe/London", "America/New_York", "America/Montreal", "Africa/Algiers", "Asia/Dubai"]; } })();

const LANG_OPTIONS = LANGUAGES.map((l) => ({ value: l.code, label: l.label }));
const UNIT_OPTIONS = ["metric", "imperial"];
const THEME_OPTIONS = ["light", "dark"];

function Row({ icon: Icon, title, children }) {
  return (
    <div className="chenia-card p-5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-2xl bg-secondary text-primary"><Icon className="h-5 w-5" /></span>
        <span className="font-medium">{title}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Seg({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-full bg-secondary p-1" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} data-testid={`${testid}-${o.value}`} onClick={() => onChange(o.value)}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${value === o.value ? "bg-primary text-primary-foreground" : "text-foreground/70"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function RemindersRow({ settings, save, paid }) {
  const { t } = useLang();
  const nav = useNavigate();
  const rem = settings.reminders || { enabled: false, time: "19:00", timezone: detectTimezone() };
  const [perm, setPerm] = useState(notifState());
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setPushOn(!!s)); }, []);
  const gate = () => { if (paid) return true; toast(t("premium_feature"), { action: { label: t("pricing"), onClick: () => nav("/pricing") } }); return false; };
  const update = (patch) => { if (gate()) save({ reminders: { enabled: rem.enabled, time: rem.time || "19:00", timezone: rem.timezone || detectTimezone(), ...patch } }); };
  const toggle = async (v) => {
    if (!gate()) return;
    update({ enabled: v });
    if (!v && pushOn && !settings.journal_reminder?.enabled) { await unsubscribePush(); setPushOn(false); }
  };
  const enablePush = async () => {
    if (!gate()) return;
    if (!pushSupported()) { toast(t("push_unsupported")); return; }
    setBusy(true);
    try { await subscribePush(rem.timezone || detectTimezone()); setPushOn(true); setPerm(notifState()); toast.success(t("push_enabled")); }
    catch (e) { setPerm(notifState()); toast.error(e.message === "denied" ? t("notifications_blocked") : t("push_failed")); }
    finally { setBusy(false); }
  };
  const disablePush = async () => { setBusy(true); try { await unsubscribePush(); setPushOn(false); toast.success(t("push_disabled")); } finally { setBusy(false); } };
  const sendTest = async () => { try { const { data } = await api.post("/push/test"); toast.success(t("push_test_sent", { n: data.sent })); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const needsHomeScreen = isIOS() && !isStandalone();

  return (
    <div className="chenia-card p-5" data-testid="settings-reminders">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-2xl bg-secondary text-primary"><Moon className="h-5 w-5" /></span>
          <div>
            <p className="font-medium inline-flex items-center gap-1.5">{t("reminders")} {!paid && <Lock className="h-3 w-3 text-muted-foreground" />}</p>
            <p className="text-xs text-muted-foreground">{t("reminders_sub")}</p>
          </div>
        </div>
        <Switch checked={paid && !!rem.enabled} onCheckedChange={toggle} data-testid="reminders-enabled" />
      </div>
      {paid && rem.enabled && (
        <div className="mt-4 space-y-3" data-testid="reminders-options">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm inline-flex items-center gap-2">{t("reminder_time")}
              <Input type="time" value={rem.time || "19:00"} onChange={(e) => update({ time: e.target.value })} className="rounded-xl w-32" data-testid="reminders-time" />
            </label>
            <label className="text-sm inline-flex items-center gap-2">{t("timezone")}
              <select value={rem.timezone || detectTimezone()} onChange={(e) => update({ timezone: e.target.value })} className="rounded-xl border border-input bg-background px-3 h-9 text-sm max-w-[220px]" data-testid="reminders-timezone">
                {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </label>
          </div>
          <div className="rounded-2xl bg-secondary/60 p-3.5 space-y-2" data-testid="push-box">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium inline-flex items-center gap-1.5"><BellRing className="h-4 w-4 text-primary" /> {t("push_title")}</p>
              {pushOn ? (
                <span className="text-xs rounded-full px-2.5 py-1 bg-chenia-sage/40" data-testid="push-status">{t("push_active")}</span>
              ) : (
                <span className="text-xs rounded-full px-2.5 py-1 bg-background text-muted-foreground" data-testid="push-status">{perm === "denied" ? t("notifications_blocked") : t("push_inactive")}</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{t("push_sub")}</p>
            {needsHomeScreen && <p className="text-xs rounded-xl bg-chenia-rosesoft/60 p-2.5" data-testid="ios-hint">{t("ios_home_screen_hint")}</p>}
            <div className="flex flex-wrap gap-2">
              {!pushOn ? (
                <Button size="sm" onClick={enablePush} disabled={busy || perm === "denied"} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="push-enable"><BellRing className="h-4 w-4 mr-1" /> {t("push_enable")}</Button>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={sendTest} className="rounded-full" data-testid="push-test">{t("push_send_test")}</Button>
                  <Button size="sm" variant="ghost" onClick={disablePush} disabled={busy} className="rounded-full text-muted-foreground" data-testid="push-disable">{t("push_disable")}</Button>
                </>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">{t("reminder_notice")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Pays de facturation (devise de l'abonnement) + abonnement courant (montant réellement facturé)
function BillingRows() {
  const { t, lang } = useLang();
  const { data } = useQuery({ queryKey: ["payments-me"], queryFn: async () => (await api.get("/payments/me")).data });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });
  if (!data || !plans) return null;
  return (
    <>
      <div className="chenia-card p-5" data-testid="settings-billing-country">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-2xl bg-secondary text-primary"><MapPin className="h-5 w-5" /></span>
            <span className="font-medium">{t("billing_country")}</span>
          </div>
          <BillingCountrySelect value={data.billing_country} countries={plans.countries} source={data.country_source} compact testId="settings-billing-country-select" />
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">{t("billing_country_help")}</p>
      </div>
      <Link to="/pricing" data-testid="settings-subscription" className="block">
        <Row icon={CreditCard} title={t("billing_subscription")}>
          <span className="flex items-center gap-2 text-sm" data-testid="settings-subscription-value">
            {data.tier === "free" ? t("billing_free_plan") : <>{t(`plan_${data.tier}`)}{data.amount != null && data.currency && <> · {formatMoney(data.amount, data.currency, lang)} {t("billing_per_month")}</>}</>}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </span>
        </Row>
      </Link>
    </>
  );
}

export default function Settings() {
  const { t, lang, setLang } = useLang();
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const s = user?.settings || {};
  const [units, setUnits] = useState(s.units || "metric");
  const [theme, setTheme] = useState(s.theme || "light");
  const [notif, setNotif] = useState(s.notifications ?? true);
  const paid = isPaid(user);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const save = async (patch) => {
    try {
      await api.put("/settings", patch);
      await refresh();
      toast.success(t("saved"));
    } catch { toast.error("Error"); }
  };

  const changeLang = (code) => { setLang(code); save({ language: code }); };
  const changeUnits = (v) => { setUnits(v); save({ units: v }); };
  const changeTheme = (v) => { setTheme(v); save({ theme: v }); };
  const changeNotif = (v) => { setNotif(v); save({ notifications: v }); };

  return (
    <AppShell>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight chenia-fade-up">{t("settings_title")}</h1>

      {/* Account */}
      <div className="chenia-card p-6 mt-8 flex items-center gap-4" data-testid="settings-account">
        <span className="grid place-items-center h-14 w-14 rounded-2xl bg-chenia-rosesoft text-chenia-terracottadark">
          {user?.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-2xl object-cover" /> : <UserRound className="h-6 w-6" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-lg">{user?.first_name}</p>
          <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
        </div>
        <Link to="/onboarding">
          <Button variant="outline" className="rounded-full" data-testid="settings-edit-profile"><Pencil className="h-4 w-4 mr-1" /> {t("edit_profile")}</Button>
        </Link>
      </div>

      <p className="mt-8 mb-3 text-sm uppercase tracking-widest text-muted-foreground font-semibold">{t("preferences")}</p>
      <div className="space-y-3">
        <Link to="/household" data-testid="settings-household" className="block">
          <Row icon={Users} title={t("household_title")}><ChevronRight className="h-4 w-4 text-muted-foreground" /></Row>
        </Link>
        {user?.role === "admin" && (
          <Link to="/admin" data-testid="settings-admin" className="block">
            <Row icon={ShieldCheck} title={t("admin_space")}><ChevronRight className="h-4 w-4 text-muted-foreground" /></Row>
          </Link>
        )}
        <Row icon={Globe} title={t("language")}>
          <Seg testid="settings-language" value={lang} onChange={changeLang}
            options={LANG_OPTIONS} />
        </Row>
        <Row icon={Ruler} title={t("units")}>
          <Seg testid="settings-units" value={units} onChange={changeUnits}
            options={UNIT_OPTIONS.map((v) => ({ value: v, label: t(v) }))} />
        </Row>
        <Row icon={SunMoon} title={t("theme")}>
          <Seg testid="settings-theme" value={theme} onChange={changeTheme}
            options={THEME_OPTIONS.map((v) => ({ value: v, label: t(v) }))} />
        </Row>
        <Row icon={Bell} title={t("notifications")}>
          <Switch checked={notif} onCheckedChange={changeNotif} data-testid="settings-notifications" />
        </Row>
        <RemindersRow settings={s} save={save} paid={paid} />
        <JournalReminderRow settings={s} save={save} />
        <BillingRows />
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Button variant="outline" onClick={async () => { await logout(); nav("/"); }}
          className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="settings-logout">
          <LogOut className="h-4 w-4 mr-1" /> {t("logout")}
        </Button>
        <Button asChild variant="ghost" className="rounded-full text-muted-foreground hover:text-destructive" data-testid="settings-delete-account">
          <Link to="/delete-account"><Trash2 className="h-4 w-4 mr-1" /> {t("del_settings_btn")}</Link>
        </Button>
      </div>
      <LegalLinks className="mt-6" testIdPrefix="settings" />

      <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{t("disclaimer")}</p>
    </AppShell>
  );
}
